import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarPlus, Loader2, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { value: "Computer reparatie aan huis",  location: "aan-huis",        label: "Aan huis (60 min)" },
  { value: "Werkplaats reparatie",         location: "werkplaats",      label: "Werkplaats — inleveren (30 min)" },
  { value: "Hulp op afstand",              location: "hulp-op-afstand", label: "Hulp op afstand (30 min)" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const AdminAppointmentDialog = ({ customer, secret, onClose, onCreated }) => {
  const [serviceIdx, setServiceIdx] = useState(0);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [description, setDescription] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState({ all_slots: [], busy_slots: [], duration_minutes: 60 });
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const svc = SERVICE_TYPES[serviceIdx];

  useEffect(() => {
    let cancel = false;
    setLoadingAvail(true);
    setTime("");
    axios.get(`${API}/appointments/availability`, { params: { date, location: svc.location } })
      .then((r) => { if (!cancel) setBusy(r.data); })
      .catch(() => { if (!cancel) setBusy({ all_slots: [], busy_slots: [], duration_minutes: 60 }); })
      .finally(() => { if (!cancel) setLoadingAvail(false); });
    return () => { cancel = true; };
  }, [date, svc.location]);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) { alert("Kies een tijd"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/appointments`, {
        customer_id: customer.id,
        service_type: svc.value,
        location: svc.location,
        description,
        date,
        time,
        notify_customer: notify,
      }, { params: { secret } });
      onCreated?.(r.data);
      onClose();
    } catch (err) {
      alert("Inplannen mislukt: " + (err.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start md:items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
              <CalendarPlus size={18}/> Afspraak inplannen
            </h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              voor <strong>{customer.company_name ? `${customer.company_name} — ${customer.name}` : customer.name}</strong>
              {customer.email && <span> · {customer.email}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="appt-close"><X size={18}/></button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Dienst</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {SERVICE_TYPES.map((s, i) => (
                <button type="button" key={s.value} onClick={() => setServiceIdx(i)}
                  className={`px-3 py-2 text-xs border text-left ${i === serviceIdx ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"}`}
                  data-testid={`appt-svc-${i}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Datum</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={todayISO()}
              className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
              data-testid="appt-date"/>
          </div>

          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Tijd ({busy.duration_minutes} min)</label>
            {loadingAvail ? (
              <div className="mt-2 text-sm text-neutral-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin"/> Beschikbaarheid laden...
              </div>
            ) : busy.all_slots.length === 0 ? (
              <p className="mt-2 text-sm text-neutral-500">Geen slots beschikbaar.</p>
            ) : (
              <div className="mt-2 grid grid-cols-6 gap-1.5">
                {busy.all_slots.map((slot) => {
                  const isBusy = busy.busy_slots.includes(slot);
                  const selected = time === slot;
                  return (
                    <button type="button" key={slot} disabled={isBusy} onClick={() => setTime(slot)}
                      className={`text-xs py-2 border ${
                        selected ? "bg-neutral-900 text-white border-neutral-900"
                        : isBusy ? "bg-neutral-100 text-neutral-300 border-neutral-200 line-through cursor-not-allowed"
                        : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"}`}
                      data-testid={`appt-slot-${slot}`}>
                      {slot}
                    </button>
                  );
                })}
              </div>
            )}
            {!busy.calendar_connected && (
              <p className="mt-2 text-xs text-amber-700">⚠ Google Calendar niet gekoppeld — slots tonen alleen lokale conflicten.</p>
            )}
          </div>

          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Omschrijving / klacht (optioneel)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
              data-testid="appt-description"/>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="appt-notify"/>
            <span>Klant per e-mail informeren met bevestiging</span>
          </label>

          {!customer.email && notify && (
            <p className="text-xs text-amber-700">⚠ Klant heeft geen e-mailadres — er wordt geen mail verstuurd.</p>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">Annuleren</button>
            <button type="submit" disabled={submitting || !time}
              className="flex items-center gap-2 px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50"
              data-testid="appt-submit">
              <Check size={14}/>{submitting ? "Inplannen..." : "Afspraak vastzetten"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAppointmentDialog;
